import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getCookie } from '../utils/utils';
import './FirstMessageSuggestions.scss';

interface Props {
  /** The conversation id used for the AI endpoint. */
  conversationId: string;
  /** Short address like "Mannerheimintie 3, Helsinki". Null if unresolvable. */
  address: string | null;
  /** Viewer's marketplace side. UNKNOWN falls back to the buyer set. */
  viewerRole: 'BUYER' | 'SELLER' | 'UNKNOWN';
  /** Current textarea value. Used to derive which bubble (if any) is highlighted. */
  value: string;
  /** Called when the user taps any bubble; receives the text to place in the
   *  composer (or '' when an already-selected static bubble is tapped to deselect). */
  onPick: (text: string) => void;
}

// Toggle the ✨ Generate with AI button. Currently disabled until the backend
// prompt produces consistently useful output. All AI plumbing below
// (AbortController, AiState machine, handleAiTap, /generate-first-message-
// suggestion endpoint, `aiButton/aiGenerating/aiFailed` i18n keys) is kept
// intact — flip this to `true` to restore.
const AI_BUTTON_ENABLED = false;

type BubbleKey =
  | 'opener'
  | 'viewing'
  | 'financials'
  | 'tellMore'
  | 'askMore';

const BUYER_KEYS: BubbleKey[] = ['opener', 'viewing', 'financials'];
const SELLER_KEYS: BubbleKey[] = ['tellMore', 'viewing', 'askMore'];

type AiState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error' };

const FirstMessageSuggestions: React.FC<Props> = ({
  conversationId,
  address,
  viewerRole,
  value,
  onPick,
}) => {
  const { t, i18n } = useTranslation();

  const variant = address ? 'withAddress' : 'generic';
  const interpolation = address ? { address } : undefined;
  const roleNamespace = viewerRole === 'SELLER' ? 'seller' : 'buyer';
  const keys = viewerRole === 'SELLER' ? SELLER_KEYS : BUYER_KEYS;

  const bubbles = keys.map((key) => ({
    key,
    text: t(
      `messages.firstMessageSuggestions.${roleNamespace}.${key}.${variant}`,
      interpolation,
    ),
  }));

  // Derived selection: whichever bubble's text exactly matches the current
  // textarea value, if any. Auto-syncs on conv switch / manual edit / toggle.
  const selectedKey: BubbleKey | null =
    bubbles.find((b) => b.text === value)?.key ?? null;

  const handlePick = (key: BubbleKey, text: string) => {
    if (selectedKey === key) {
      // Tap the already-selected bubble → clear the textarea preview.
      onPick('');
      return;
    }
    onPick(text);
  };

  // ── AI generate button ──────────────────────────────────────────────
  // Calls POST /conversations/<id>/generate-first-message-suggestion (BE
  // pinned to gpt-5.5-mini, gpt-5.4-mini fallback). Each tap generates a
  // fresh opener and overwrites the textarea. Not a toggle — re-tapping
  // re-generates rather than clearing.
  const abortRef = useRef<AbortController | null>(null);
  const errorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [aiState, setAiState] = useState<AiState>({ status: 'idle' });

  const handleAiTap = useCallback(async () => {
    // Atomically claim loading state; no-op if a tap is in flight.
    let alreadyLoading = false;
    setAiState((prev) => {
      if (prev.status === 'loading') {
        alreadyLoading = true;
        return prev;
      }
      return { status: 'loading' };
    });
    if (alreadyLoading) return;

    if (abortRef.current) abortRef.current.abort();
    const ac = new AbortController();
    abortRef.current = ac;

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/conversations/${conversationId}/generate-first-message-suggestion`,
        {
          method: 'POST',
          credentials: 'include',
          signal: ac.signal,
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': getCookie('csrf_access_token'),
          },
          body: JSON.stringify({ language: i18n.language === 'en' ? 'en' : 'fi' }),
        },
      );
      if (!res.ok) throw new Error(`status_${res.status}`);
      const data = (await res.json()) as { suggestion?: string };
      if (!data.suggestion) throw new Error('empty');
      onPick(data.suggestion);
      setAiState({ status: 'idle' });
    } catch (e) {
      if ((e as Error).name === 'AbortError') return;
      setAiState({ status: 'error' });
      if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
      errorTimerRef.current = setTimeout(() => {
        errorTimerRef.current = null;
        setAiState((prev) => (prev.status === 'error' ? { status: 'idle' } : prev));
      }, 4000);
    }
  }, [conversationId, i18n.language, onPick]);

  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
      if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    };
  }, []);

  const aiLabel =
    aiState.status === 'loading'
      ? t('messages.firstMessageSuggestions.aiGenerating')
      : aiState.status === 'error'
      ? t('messages.firstMessageSuggestions.aiFailed')
      : t('messages.firstMessageSuggestions.aiButton');

  return (
    <div className="first-message-suggestions">
      <div className="first-message-suggestions__header">
        <span
          className="first-message-suggestions__header-icon"
          aria-hidden="true"
        >
          {/* Filled multi-star sparkles icon (Heroicons solid). Matches Sebastian's mockup. */}
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M9 4.5a.75.75 0 0 1 .721.544l.813 2.846a3.75 3.75 0 0 0 2.576 2.576l2.846.813a.75.75 0 0 1 0 1.442l-2.846.813a3.75 3.75 0 0 0-2.576 2.576l-.813 2.846a.75.75 0 0 1-1.442 0l-.813-2.846a3.75 3.75 0 0 0-2.576-2.576l-2.846-.813a.75.75 0 0 1 0-1.442l2.846-.813A3.75 3.75 0 0 0 7.466 7.89l.813-2.846A.75.75 0 0 1 9 4.5ZM18 1.5a.75.75 0 0 1 .728.568l.258 1.036c.236.94.97 1.674 1.91 1.91l1.036.258a.75.75 0 0 1 0 1.456l-1.036.258a2.625 2.625 0 0 0-1.91 1.91l-.258 1.036a.75.75 0 0 1-1.456 0l-.258-1.036a2.625 2.625 0 0 0-1.91-1.91l-1.036-.258a.75.75 0 0 1 0-1.456l1.036-.258a2.625 2.625 0 0 0 1.91-1.91l.258-1.036A.75.75 0 0 1 18 1.5ZM16.5 15a.75.75 0 0 1 .712.513l.394 1.183c.15.447.5.799.948.948l1.183.395a.75.75 0 0 1 0 1.422l-1.183.395c-.447.15-.799.5-.948.948l-.395 1.183a.75.75 0 0 1-1.422 0l-.395-1.183a1.5 1.5 0 0 0-.948-.948l-1.183-.395a.75.75 0 0 1 0-1.422l1.183-.395c.447-.15.799-.5.948-.948l.395-1.183A.75.75 0 0 1 16.5 15Z"
            />
          </svg>
        </span>
        <div className="first-message-suggestions__header-text">
          <div className="first-message-suggestions__header-title">
            {t('messages.firstMessageSuggestions.header')}
          </div>
          <div className="first-message-suggestions__header-subtitle">
            {t('messages.firstMessageSuggestions.subheader')}
          </div>
        </div>
      </div>
      {bubbles.map(({ key, text }) => {
        const isSelected = selectedKey === key;
        return (
          <button
            key={key}
            type="button"
            className={
              'first-message-suggestions__bubble' +
              (isSelected ? ' first-message-suggestions__bubble--selected' : '')
            }
            onClick={() => handlePick(key, text)}
            aria-pressed={isSelected}
          >
            <span className="first-message-suggestions__bubble-text">
              {text}
            </span>
            {isSelected && (
              <span
                className="first-message-suggestions__bubble-check"
                aria-hidden="true"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="11" fill="#2563eb" />
                  <path
                    d="M7 12.5L10.5 16L17 9"
                    stroke="#ffffff"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  />
                </svg>
              </span>
            )}
          </button>
        );
      })}
      {/* JAA-67: ✨ Generate with AI button is temporarily disabled until the
          prompt produces consistently useful output. State machine + endpoint
          wiring are kept intact below — flip AI_BUTTON_ENABLED to `true` to
          restore. Tracked separately in a follow-up ticket. */}
      {AI_BUTTON_ENABLED && (
        <button
          type="button"
          className="first-message-suggestions__bubble first-message-suggestions__bubble--ai"
          onClick={handleAiTap}
          disabled={aiState.status === 'loading'}
          aria-busy={aiState.status === 'loading'}
        >
          <span
            className="first-message-suggestions__bubble-ai-icon"
            aria-hidden="true"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M9 4.5a.75.75 0 0 1 .721.544l.813 2.846a3.75 3.75 0 0 0 2.576 2.576l2.846.813a.75.75 0 0 1 0 1.442l-2.846.813a3.75 3.75 0 0 0-2.576 2.576l-.813 2.846a.75.75 0 0 1-1.442 0l-.813-2.846a3.75 3.75 0 0 0-2.576-2.576l-2.846-.813a.75.75 0 0 1 0-1.442l2.846-.813A3.75 3.75 0 0 0 7.466 7.89l.813-2.846A.75.75 0 0 1 9 4.5ZM18 1.5a.75.75 0 0 1 .728.568l.258 1.036c.236.94.97 1.674 1.91 1.91l1.036.258a.75.75 0 0 1 0 1.456l-1.036.258a2.625 2.625 0 0 0-1.91 1.91l-.258 1.036a.75.75 0 0 1-1.456 0l-.258-1.036a2.625 2.625 0 0 0-1.91-1.91l-1.036-.258a.75.75 0 0 1 0-1.456l1.036-.258a2.625 2.625 0 0 0 1.91-1.91l.258-1.036A.75.75 0 0 1 18 1.5ZM16.5 15a.75.75 0 0 1 .712.513l.394 1.183c.15.447.5.799.948.948l1.183.395a.75.75 0 0 1 0 1.422l-1.183.395c-.447.15-.799.5-.948.948l-.395 1.183a.75.75 0 0 1-1.422 0l-.395-1.183a1.5 1.5 0 0 0-.948-.948l-1.183-.395a.75.75 0 0 1 0-1.422l1.183-.395c.447-.15.799-.5.948-.948l.395-1.183A.75.75 0 0 1 16.5 15Z"
              />
            </svg>
          </span>
          <span className="first-message-suggestions__bubble-text">{aiLabel}</span>
        </button>
      )}
    </div>
  );
};

export default FirstMessageSuggestions;
